<!--
	@name app-conference
	@description Conference page
	@date 2020/05/20
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<div class="hero">
			<v-container fill-height>
				<v-row no-gutters>
					<v-col cols="12" sm="12" class="text-center text-md-left">
						<v-row no-gutters class="mb-10 justify-center justify-md-start">
							<v-col class="shrink">
								<div class="text-h5 text-lg-h4 white--text font-weight-light nowrap">
									Air Conference 10<sup>th</sup> June
								</div>
							</v-col>
							<v-col class="shrink d-none d-lg-block">
								<div class="text-h4 text-lg-h4 white--text mx-4">
									|
								</div>
							</v-col>
							<v-col class="shrink d-none d-lg-block">
								<img :src="'manager_documents/4e73e9e6-a484-4bf6-9e23-17fa06e2dc71.png' | urlize" style="height: 30px;" />
							</v-col>
						</v-row>
						<div class="text-h3 text-lg-h2 text-lg-h2--alt white--text font-weight-bold text-uppercase mb-4">
							The Road to Success
						</div>
						<div class="text-h5 text-lg-h4 text-lg-h4--alt white--text font-weight-regular">
							Begins with Comprehensive Conversations
						</div>
					</v-col>
				</v-row>
			</v-container>
		</div>

		<v-container class="section0 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-center d-flex align-center flex-column px-md-10">
				<p class="text-h5 text-md-h4 text-lg-h3 font-weight-bold mb-6" :style="{ 'line-height': $vuetify.breakpoint.lgAndUp ? '3.75rem' : 'auto' }">
					Unlock your potential and create a better future for your customers
				</p>
				<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-4">
					This year's annual Air Conference placed the spotlight firmly on the customer— with main plenaries focusing on who they are, how you can best identify and understand them, and the strategies needed to acquire and nurture lasting relationships.
				</p>
				<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-0">
					If you're looking for support on lead generation, improving advice efficiency with the latest tech and being on the front foot on all of the latest insights in later life lending, then take time to catch up with the sessions on-demand
				</p>
				<div class="mt-15 mt-md-12 mx-n3">
					<v-btn :large="$vuetify.breakpoint.mdAndUp" target="_blank" color="primary" dark class="mx-3" @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Making the Complex Simple' })">
						request a callback
					</v-btn>
				</div>
			</div>
		</v-container>

		<v-divider />

		<conference-section
			v-for="section, index in sections"
			:id="section.id"
			:key="index"
			:title="section.title"
			:subtitle="section.subtitle"
			:vimeo="section.vimeo"
			:reverse="index%2===0"
			avatar-display="none"
			@handle-cta-click="handleCtaClick"
		>
			<template #copy>
				<slot>
					<span v-html="section.copy" />
					<div class="ma-n3 d-lg-flex flex-wrap pt-5">
						<conference-avatar
							v-for="avatar in section.avatars"
							:key="avatar.name"
							:name="avatar.name"
							:position="avatar.position"
							:image="avatar.image"
							:size="avatarSize"
							:basis="avatarBasis"
							:dark="dark"
						/>
					</div>
					<div v-if="section.cta" class="mt-8">
						<v-btn
							target="_blank"
							color="primary"
							dark
							@click="handleCtaClick(section.cta.action)"
						>
							{{ section.cta.title }}
						</v-btn>
					</div>
				</slot>
			</template>
		</conference-section>

		<v-divider />

		<!-- Plenary 3 -->
		<div>
			<!-- div class="car" / -->
			<conference-section
				:id="'plenary3'"
				:title="'Engineer and F1 TV presenter Ruth Buscombe:'"
				:subtitle="'Strategy at Speed: Formula 1 Lessons to Be Fit for the Future'"
				:avatar-size="'large'"
				:avatar-basis="100"
				:members-only="true"
				:member="!!auth"
				:reverse="true"
				:divider="false"
				class="asphalt--content"
				@login="login"
			>
				<template #copy>
					<slot>
						<p class="text-body-1 mb-4">
							In a world moving faster than ever, standing still isn't an option. This high-energy session drew lessons from Formula 1 to help advisers and business leaders stay ahead.
							Featuring F1 strategist and broadcaster Ruth Buscombe, it explored how technology, AI, and split-second decisions drive performance - on the track and in business. With insights from real races like the Brazilian Grand Prix, the session highlighted the power of preparation, agility, and continuous improvement.
						</p>
					</slot>
				</template>
			</conference-section>
		</div>

		<div id="contact" ref="contact" class="header--waves scroll-margin--fixed-header-offset">
			<!--Content before waves-->
			<div class="inner-header flex">
				<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }">
					<div v-if="!success">
						<p class="white--text text-center text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6">
							Unlock your potential with Air.
						</p>
						<p class="white--text text-body-1 bypass--line-height--override text-md-h6 font-weight-regular text-center mb-10">
							Discover more opportunities with the <a class="font-weight-black" @click="$router.push('/navigator')">Air Navigator Tool</a>,
							helping you to identify the right later life lending or residential mortgage options for your clients.
							Plus, elevate your expertise with <a class="font-weight-black" @click="$router.push('/air-academy')">Air Academy</a>,
							offering courses and resources to support your professional growth. <span class="font-weight-black">Request a call back to learn more!</span>
						</p>
					</div>
					<div class="text-md-center d-flex align-center flex-column">
						<div class="grey lighten-4 pa-8 rounded elevation-6" style="max-width: 700px;">
							<v-fade-transition>
								<div v-if="success">
									<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-4 mx-auto" style="max-width: 500px;">
										Thank you for reaching out!
									</p>
									<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-6 mx-auto" style="max-width: 500px;">
										Your email has been sent. We'll get back to you shortly. In the meantime, check out <a class="ga--trigger ga--campaign-click ga--events" @click="$router.push('/events')">upcoming events</a> or read more <a class="ga--trigger ga--campaign-click ga--academy" @click="$router.push('/air-academy')">about us</a>.
									</p>
									<v-icon color="success" size="60">
										mdi-check-circle
									</v-icon>
								</div>
							</v-fade-transition>
							<template v-if="!success">
								<validation-observer ref="observer">
									<v-form @submit.prevent="submit">
										<v-row>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="First Name" rules="required">
													<v-text-field v-model="firstName" :disabled="disabled" :error-messages="errors" label="First Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="Last Name" rules="required">
													<v-text-field v-model="lastName" :disabled="disabled" :error-messages="errors" label="Last Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Firm Name" rules="required">
													<v-text-field v-model="firmName" :disabled="disabled" :error-messages="errors" label="Firm Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="FCA Number" rules="required|numeric|min:6|max:7">
													<v-text-field v-model="fcaNumber" :disabled="disabled" :error-messages="errors" label="FCA Number" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Email" rules="required|email">
													<v-text-field v-model="email" :disabled="disabled" :error-messages="errors" label="Email" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Phone" rules="required|numeric">
													<v-text-field v-model="phone" :disabled="disabled" :error-messages="errors" label="Phone" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<v-btn class="ga--trigger ga--campaign-click ga--form-submit white--text" color="primary" :disabled="disabled" :loading="loading" type="submit" large block>
													Submit for a call back
												</v-btn>
											</v-col>
										</v-row>
									</v-form>
								</validation-observer>
							</template>
						</div>
					</div>
				</v-container>
			</div>

			<!--Waves Container-->
			<div>
				<svg
					class="waves"
					xmlns="http://www.w3.org/2000/svg"
					xmlns:xlink="http://www.w3.org/1999/xlink"
					viewBox="0 24 150 28"
					preserveAspectRatio="none"
					shape-rendering="auto"
				>
					<defs>
						<path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
					</defs>
					<g class="parallax">
						<use xlink:href="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
						<use xlink:href="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)" />
						<use xlink:href="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)" />
						<use xlink:href="#gentle-wave" x="48" y="7" fill="#fff" />
					</g>
				</svg>
			</div>
			<!--Waves end-->
		</div>
		<div class="body--waves d-flex" />
		<common-dialog-login ref="login" @login-success="loginSuccess" />
	</div>
</template>

<script>
	import { mapActions } from 'vuex';
	import { EventBus, ElementTools } from '@/utils';
	import ConferenceSection from '@/component/app/air-later-life/conference/conference-2025/section';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';
	import CommonDialogLogin from '@/component/common/dialog/login';

	export default {
		name: 'app-conference',

		components: {
			ConferenceSection,
			ValidationObserver,
            ValidationProvider,
			CommonDialogLogin
		},

		data() {
			return {
				auth: null,
				sections: [
					{
						id: "introduction",
						subtitle: "Air Event Opener",
						copy: `<p class="text-body-1 mb-0">In his keynote, Air's CEO, Will Hale, welcomed members to the 2025 Air Conference with a clear focus on collaboration, innovation, and customer-first thinking in later life lending. He shared insights on market recovery, regulatory expectations, and Air's commitment to supporting our members through inclusive tools, partnerships, and practical resources—helping them to deliver good customer outcomes and grow their business. <strong>Want to learn more about how Air can support your business? Request a call back and a member of our team will be in touch.</strong></p>`,
						vimeo: "1094349668",
						cta: {
							title: 'Request a callback',
							action: { function: 'arrangeDemo', subject: 'Learn about how Air can support your business' }
						}
					},
					{
						id: "plenary1",
						title: "Plenary 1",
						subtitle: "Keynote:  In the Driver's Seat: Understanding the Over-50s Customer.",
						copy: `<p class="text-body-1 mb-0">The over-50s market is often grouped together — but today's customers in this segment are anything but uniform. They are working longer, spending differently, adopting new technologies, and rethinking what later life looks like. This keynote unpacks the evolving identity of the over-50s customer. <strong>Get ahead and understand your over-50s customers, and discover how Air can support you in meeting evolving client needs—request a call back to explore the next steps.</strong></p>`,
						vimeo: "1094350178",
						cta: {
							title: 'Request a callback',
							action: { function: 'arrangeDemo', subject: 'Understanding the Over-50s Customer' }
						}
					},
					{
						id: "plenary2",
						title: "Plenary 2",
						subtitle: "From Pit Lane to Pole Position: Winning Over-50s Customer Acquisition.",
						copy: `<p class="text-body-1 mb-0">From AI-driven customer segmentation to purchased leads, firms have new tools available, but these must be used ethically and effectively. Acquisition strategies must align closely with real customer needs and regulatory expectations. This discussion highlighted practical, insight-led approaches for smarter, more sustainable customer acquisition. <strong>Speak with Air to explore how we can help you to expand you to unlock expert marketing strategies today.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Discover Air Marketing',
							action: { route: '/marketing' }
						}
					},
					{
						id: "lead-generation",
						title: "Pit Stop: Clinic",
						subtitle: "Lead Generation Tune-Up",
						copy: `<p class="text-body-1 mb-0">Hear from Air Marketing and specialist lead generation experts, to discover actionable strategies, advanced resources, and effective tools designed to fuel sustainable business growth. <strong>Unlock expert lead generation tools and support with Air Marketing.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Discover Air Marketing',
							action: { route: '/marketing' }
						}
					},
					{
						id: "tech-lap",
						title: "Pit Stop: Clinic",
						subtitle: "Take A Tech Lap",
						copy: `<p class="text-body-1 mb-0">Engage with innovative digital tools and expert insights designed to enhance your fact-finding process and product sourcing capabilities. Gain practical skills to use WriteRoute and Air Sourcing. Plus, hear from more2life about their new technology ProView. <strong>Discover how Air's best-in-class sourcing platform and fact-find tool, WriteRoute can keep you ahead.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Join A Training Webinar',
							action: { route: '/events?category=Training' }
						}
					},
					{
						id: "compliance-checkpoint",
						title: "Pit Stop: Clinic",
						subtitle: "Compliance Checkpoint",
						copy: `<p class="text-body-1 mb-0">Hear from compliance specialist network Paradigm and explore best practices for ensuring robust and compliant client journeys. Plus, experience Air Flow, our revolutionary integrated technology within Air Sourcing helping you to streamline the customer journey with seamless workflows, instant documentation to support you with your compliance process. <strong>Looking to streamline your business processes? Discover how AirFlow can support you.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Request a callback',
							action: { function: 'arrangeDemo', subject: 'Compliance Checkpoint' }
						}
					},
					{
						id: "accelerate",
						title: "Pit Stop: Clinic",
						subtitle: "Accelerate Your Knowledge",
						copy: `<p class="text-body-1 mb-0">This educational session provided attendees with targeted learning opportunities from Air Academy, Aviva and the Equity Release Council. Attendees deepened their industry knowledge and strengthened their advisory skills through tailored educational programmes, including the recent Fact-find Learning Programme. <strong>Connect with Air to explore how our Academy learning platform and put your personal development in pole position.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Explore Air Academy',
							action: { route: '/air-academy' }
						}
					},
					{
						id: "case-study",
						subtitle: "Case Study & Interactive Fact-Find",
						copy: `<p class="text-body-1 mb-0">This session was designed to sharpen advisers' skills in navigating complex client scenarios through a live role-play with a professional actress portraying a later life lending client. Advisers practiced asking thoughtful questions, listening actively, and uncovering financial goals - while focusing on key areas like health, affordability, and vulnerability. <strong>Discover how Air can support you in meeting evolving client needs—request a call back to explore the next steps.</strong></p>`,
						// vimeo: "1053461981",
						cta: {
							title: 'Request a callback',
							action: { function: 'arrangeDemo', subject: 'Case Study and Interactive Fact-Find' }
						}
					}
				],
				avatarBasis: 50,
				avatarSize: 'medium',
				avatarDisplay: 'none',
				dark: false,
                campaign: 'Conference Page',
                subject: null,
                firstName: '',
				lastName: '',
				firmName: '',
				fcaNumber: '',
				email: '',
				phone: '',
				message: '',
				loading: false,
				success: false,
				disabled: false
			};
		},

		computed: {

		},

		mounted() {
			let hash = this.$route.hash;
			if(hash) this.scrollToAnchorPoint(hash.replace('#', ''));
		},

		async created() {
			this.auth = window.localStorage.getItem('air.authorization');
		},

		methods: {
			...mapActions('ContactCampaign', ['submitContactCampaign']),

			scrollToAnchorPoint(refName, subject = null) {
				if (subject) this.subject = subject;
                document.getElementById(refName).scrollIntoView({ behavior: 'smooth'});
            },

			handleCtaClick(cta) {
				if (cta.function) {
					let fnctn = cta.function;
					if (fnctn === 'arrangeDemo') this.scrollToAnchorPoint('contact', cta.subject || null);
					if (fnctn === 'openChat') EventBus.$emit('open-chat');
				} else if (cta.route) {
					let route = cta.route;
					if (this.auth && cta.authRoute) {
						route = cta.authRoute;
						this.$router.push(route);
					} else {
						this.$router.push(route);
					}
				}
			},

			submit() {
				this.$refs.observer.validate().then(async(valid) => {
					if (!valid) return false;

					this.success = false;
					this.loading = true;

					this.submitContactCampaign({
						firstName: this.firstName,
						lastName: this.lastName,
						firmName: this.firmName,
						fcaNumber: this.fcaNumber,
						email: this.email,
						phone: this.phone,
						campaign: this.campaign,
						subject: this.subject
					})
						.then(() => {
							this.success = true;
							this.resetForm();
						})
						.catch(() => ElementTools.fireNotification(this.$el, 'error', 'An error occurred. Please try again later.'))
						.finally(() => {
							this.subject = null;
							this.loading = false;
						});
				});
			},

			resetForm() {
				this.subject = null;
				this.firstName = '';
				this.lastName = '';
				this.firmName = '';
				this.fcaNumber = '';
				this.email = '';
				this.phone = '';
				this.disabled = true;
				this.$refs.observer.reset();
			},

			login() {
				this.$refs.login.open('Title', `Are you sure...`);
			},

			loginSuccess() {
				this.auth = window.localStorage.getItem('air.authorization');
				this.$refs.login.close();
			}
		}
	};
</script>

<style lang="scss" scoped>
    .hero {
        height: 450px;
		background-position: 950px 50%, 0%;
		background-size: contain;
		background-repeat: no-repeat;
		background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/f776a6a1-a9a5-4760-a593-4fece1eee6e1.png'), linear-gradient(0, #302875 0%, #5354c0 100%);

		@media #{map-get($display-breakpoints, 'lg-and-up')} {
			.text-lg-h2--alt {
				font-size: 4.5rem !important;
				line-height: 4.5rem !important;
			}
			.text-lg-h4--alt {
				font-size: 2.5rem !important;
				line-height: 3rem !important;
			}
		}
	}

	.asphalt {
		position: relative;
		background-color: CCCCCC;

		.car {
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/cms/clipart4510703.png');
			background-repeat: no-repeat;
			background-position: -250% 50%;
			background-attachment: fixed;
			background-size: 20%;
			animation: animatedBackground 20s ease-in-out infinite;
			opacity: 0.4;
		}

		@keyframes animatedBackground {
			0% {
				background-position: -250% 50%;
			}
			25% {
				background-position: 250% 50%;
			}
			100% {
				background-position: 250% 50%;
			}
		}

		&:before {
			content: ' ';
			display: block;
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			opacity: 0.15;
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/cms/asphalt-road-texture-dark-gray-color.jpg');
			background-repeat: no-repeat;
			background-position: 50% 0;
			background-size: cover;
			background-attachment: fixed;
		}

		&--content {
			position: relative;
		}
	}

	@function tint($color, $percentage) {
		@return mix(white, $color, $percentage);
	}

	// Variables
	$lato: 'Roboto';
	$cardCol1: tint(rgb(27, 110, 216), 0%);
	$cardCol2: tint(rgb(27, 110, 216), 10%);

	.wrap {
		background-position: 50% 50%;
		background-size: cover;
	}

	.header--waves {
        background-position: 50% 50%;
        background-size: cover;
        background-attachment: fixed;
        background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/d0bb7b46-b858-45ac-8e11-15f9c7883eb7.png');
		background-color: #42409b;
		background-blend-mode: darken;
        position:relative;
    }

    .inner-header {
        width:100%;
        margin: 0;
        padding: 0;
    }

    .waves {
        position:relative;
        width: 100%;
        height:50px;
        margin-bottom:-7px; /*Fix for safari gap*/
        min-height:50px;
        max-height:50px;
    }

    .body--waves {
        position:relative;
        height:50px;
        text-align:center;
        background-color: white;
    }

	    /* Animation */
		.parallax > use {
        animation: move-forever 25s cubic-bezier(.55,.5,.45,.5)     infinite;
    }
    .parallax > use:nth-child(1) {
        animation-delay: -2s;
        animation-duration: 7s;
    }
    .parallax > use:nth-child(2) {
        animation-delay: -3s;
        animation-duration: 10s;
    }
    .parallax > use:nth-child(3) {
        animation-delay: -4s;
        animation-duration: 13s;
    }
    .parallax > use:nth-child(4) {
        animation-delay: -5s;
        animation-duration: 20s;
    }
    @keyframes move-forever {
        0% {
        transform: translate3d(-90px,0,0);
        }
        100% {
            transform: translate3d(85px,0,0);
        }
    }
    /*Shrinking for mobile*/
    @media (max-width: 768px) {
        .waves {
            height:40px;
            min-height:40px;
        }
        .body--waves {
            height:30vh;
        }
    }

	.scroll-margin--fixed-header-offset {
        scroll-margin: 140px;
    }
</style>
